import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { getUser } from "../lib/auth";
import { Comments } from "../components/Comments";
import { ArrowLeft, Clock, Sparkles, Rss, Tag, Share2, ExternalLink, Bookmark } from "lucide-react";
import { toast } from "sonner";

export default function ArticleDetail() {
  const { slug } = useParams();
  const user = getUser();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => {
      setArticle(r.data);
      if (getUser()) {
        api.get("/bookmarks/ids").then((res) => setBookmarked(res.data.includes(r.data.id))).catch(() => {});
      }
    }).catch(() => setNotFound(true));
  }, [slug]);

  const toggleBookmark = async () => {
    if (!user) {
      toast.info("Masuk dulu untuk menyimpan artikel");
      return;
    }
    try {
      const r = await api.post(`/bookmarks/${article.id}`);
      setBookmarked(r.data.bookmarked);
      toast.success(r.data.bookmarked ? "Artikel disimpan" : "Bookmark dihapus");
    } catch {
      toast.error("Gagal menyimpan bookmark");
    }
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: article.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link disalin ke clipboard");
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center relative z-10">
        <h1 className="font-heading text-3xl font-bold">Artikel tidak ditemukan</h1>
        <Link to="/articles" className="btn-primary mt-6">Kembali ke berita</Link>
      </div>
    );
  }
  if (!article) return <div className="text-center py-24 text-slate-500">Memuat...</div>;

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
      <Link to="/articles" className="text-cyan1 text-sm inline-flex items-center gap-2 mb-8" data-testid="back-to-articles">
        <ArrowLeft className="w-4 h-4" /> Kembali ke berita
      </Link>

      <div className="flex gap-2 mb-4 flex-wrap">
        <span className="chip chip-cyan">{article.category}</span>
        {article.source === "ai" && <span className="chip chip-magenta"><Sparkles className="w-3 h-3" /> AI Generated</span>}
        {article.source === "rss" && <span className="chip chip-green"><Rss className="w-3 h-3" /> RSS</span>}
      </div>

      <h1 className="font-heading text-4xl md:text-5xl font-black leading-tight" data-testid="article-title">{article.title}</h1>

      <div className="mt-4 flex items-center gap-4 text-xs font-mono uppercase tracking-widest text-slate-500 border-b border-white/10 pb-6">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.reading_time} min read</span>
        <span>·</span>
        <span>{new Date(article.published_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</span>
        <span>·</span>
        <span>{article.author}</span>
        <button onClick={toggleBookmark} className={`ml-auto btn-ghost !py-1 !px-3 text-xs ${bookmarked ? "!border-cyan1 !text-cyan1" : ""}`} data-testid="bookmark-article">
          <Bookmark className={`w-3 h-3 ${bookmarked ? "fill-current" : ""}`} /> {bookmarked ? "Tersimpan" : "Simpan"}
        </button>
        <button onClick={share} className="btn-ghost !py-1 !px-3 text-xs" data-testid="share-article">
          <Share2 className="w-3 h-3" /> Bagikan
        </button>
      </div>

      {article.image_url && (
        <div className="mt-8 relative">
          <div className="absolute -inset-1 bg-gradient-to-br from-cyan1/30 to-magenta1/30 rounded-2xl blur-2xl" />
          <img src={article.image_url} alt="" className="relative w-full max-h-[480px] object-cover rounded-2xl border border-white/10" />
        </div>
      )}

      {article.ai_summary && (
        <div className="mt-8 rounded-2xl p-6 relative bg-gradient-to-br from-cyan1/5 to-magenta1/5 border border-cyan1/30" data-testid="ai-summary-box">
          <div className="chip chip-magenta mb-3"><Sparkles className="w-3 h-3" /> AI Summary</div>
          <p className="text-slate-200 text-base leading-relaxed">{article.ai_summary}</p>
        </div>
      )}

      <div className="prose-neon mt-10 max-w-none" data-testid="article-content">
        <ReactMarkdown>{article.content}</ReactMarkdown>
      </div>

      {article.tags?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-2">
          <Tag className="w-4 h-4 text-slate-500" />
          {article.tags.map((t) => (
            <Link key={t} to={`/articles?search=${encodeURIComponent(t)}`} className="chip hover:border-cyan1/50 hover:text-cyan1">#{t}</Link>
          ))}
        </div>
      )}

      {article.source_url && (
        <div className="mt-8">
          <a href={article.source_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="external-source">
            <ExternalLink className="w-4 h-4" /> Sumber asli
          </a>
        </div>
      )}

      <Comments articleId={article.id} />
    </article>
  );
}
